/**
 * React Query hooks for autocomplete-related API endpoints
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { dataApiClient } from '../client'
import { staticCacheConfig, createCacheKey } from '../../../lib/utils'
import type { AutocompleteRequest } from '../'

/**
 * Hook to perform autocomplete search with debouncing support
 */
export function useAutocomplete(
  request: AutocompleteRequest,
  options?: {
    enabled?: boolean
    staleTime?: number
    gcTime?: number
  }
) {
  return useQuery({
    queryKey: createCacheKey('autocomplete', request),
    queryFn: async () => {
      const { data, error } = await dataApiClient.POST('/autocomplete', {
        body: request,
      })

      if (error) {
        throw new Error(`Failed to perform autocomplete: ${error}`)
      }

      return data
    },
    enabled: options?.enabled ?? true,
    staleTime: options?.staleTime ?? staticCacheConfig.staleTime,
    gcTime: options?.gcTime ?? staticCacheConfig.gcTime,
  })
}

/**
 * Hook to prefetch autocomplete results
 */
export function usePrefetchAutocomplete() {
  const queryClient = useQueryClient()

  return (request: AutocompleteRequest) => {
    queryClient.prefetchQuery({
      queryKey: createCacheKey('autocomplete', request),
      queryFn: async () => {
        const { data, error } = await dataApiClient.POST('/autocomplete', {
          body: request,
        })

        if (error) {
          throw new Error(`Failed to prefetch autocomplete: ${error}`)
        }

        return data
      },
      staleTime: staticCacheConfig.staleTime,
    })
  }
}

/**
 * Utility hook to invalidate autocomplete cache
 */
export function useInvalidateAutocomplete() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['autocomplete'],
    })
  }
}
